import React from 'react';
import { Container, Grid, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PageLayout from '../components/PageLayout';
import Section, { SectionHeading } from '../components/PageSection';
import { accentAt, ACCENTS } from '../components/ui';

const SITE = 'https://qasc.it';

// The shared <Contact /> section (form + map + sedi, id="rs-contact") is
// rendered by PageLayout right below, so this page only adds the recap cards.

const InfoCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  padding: '32px',
  height: '100%',
  border: '1px solid rgba(0,66,92,0.10)',
  boxShadow: '0 6px 20px rgba(0,66,92,0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 16px 34px rgba(0,66,92,0.12)',
  },
}));

const InfoIcon = styled(Box, { shouldForwardProp: (p) => p !== 'accent' })(
  ({ theme, accent = 'petrol' }) => ({
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: (ACCENTS[accent] || ACCENTS.petrol).bg,
    color: (ACCENTS[accent] || ACCENTS.petrol).fg,
    marginBottom: '20px',
    '& svg': { fontSize: '28px' },
  })
);

const InfoTitle = styled(Typography)(({ theme }) => ({
  fontFamily: '"Sora", sans-serif',
  fontSize: '18px',
  fontWeight: 700,
  color: '#00425c',
  marginBottom: '8px',
}));

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: '14.5px',
  lineHeight: 1.7,
  color: '#48606d',
}));

const InfoLink = styled('a')(({ theme }) => ({
  color: '#00425c',
  fontWeight: 600,
  textDecoration: 'none',
  '&:hover': { textDecoration: 'underline' },
}));

const details = [
  {
    icon: <LocationOnIcon />,
    title: 'Sede operativa e aule formative',
    body: (
      <>
        Viale Sanremo, 36
        <br />
        30038 Spinea (VE)
        <br />
        Telefono <InfoLink href="tel:+39041995202">+39 041 995202</InfoLink>
      </>
    ),
  },
  {
    icon: <PhoneIcon />,
    title: 'Sede operativa',
    body: (
      <>
        Via Monte Nero, 4
        <br />
        67043 Celano (AQ)
        <br />
        Telefono / Fax <InfoLink href="tel:+390863792752">+39 0863 792752</InfoLink>
      </>
    ),
  },
  {
    icon: <EmailIcon />,
    title: 'E-mail',
    body: (
      <>
        <InfoLink href="mailto:info@qasc.it">info@qasc.it</InfoLink>
        <br />
        <InfoLink href="mailto:formazione@qasc.it">formazione@qasc.it</InfoLink>
      </>
    ),
  },
  {
    icon: <AccessTimeIcon />,
    title: 'Orari',
    body: <>I nostri uffici sono aperti dal lunedì al venerdì, dalle ore 08:00 alle ore 18:00.</>,
  },
];

function ContattiPage() {
  return (
    <PageLayout
      seo={{
        title: 'Contatti | QAS Consulting',
        description:
          'Richiedici maggiori informazioni per personalizzare i nostri servizi di formazione e consulenza. Sedi a Spinea (VE), tel. +39 041 995202, e a Celano (AQ), tel. +39 0863 792752 — info@qasc.it.',
        canonical: `${SITE}/contatti`,
      }}
      hero={{
        kicker: 'Contatti',
        title: 'Contattaci',
        subtitle:
          'Richiedici maggiori informazioni per personalizzare, in base alle tue esigenze, i nostri servizi di formazione e consulenza.',
      }}
    >
      <Section tone="light">
        <Container maxWidth="lg">
          <SectionHeading
            kicker="QAS Consulting"
            title="Le nostre sedi e i nostri recapiti"
          />
          <Grid container spacing={4}>
            {details.map((d, i) => (
              <Grid item xs={12} sm={6} md={3} key={d.title}>
                <InfoCard>
                  <InfoIcon accent={accentAt(i)}>{d.icon}</InfoIcon>
                  <InfoTitle>{d.title}</InfoTitle>
                  <InfoText component="div">{d.body}</InfoText>
                </InfoCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Section>
    </PageLayout>
  );
}

export default ContattiPage;
